import { useState, useEffect, useRef, useMemo } from "react";
import ImageUpload from "@/components/ImageUpload";
import { useAuth } from "@/contexts/AuthContext";
import {
  getProfile,
  updateProfile,
  getLocationStatus,
  requestAndUpdateGPS,
  ipLocationFallback,
  api,
  getCountries
} from "@/lib/api";
import { toast } from "sonner";
import {
  Save, Edit3, X, Camera, MapPin, GraduationCap, Baby, Heart, Briefcase,
  Wine, Cigarette, User, AlertCircle, RefreshCw, Trash2, Lock, Phone,
  ShieldCheck, Mail
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// ── Country Codes Array (Required for the Phone Number Dropdown) ──
const COUNTRY_CODES = [
  { code: "+27", name: "South Africa", flag: "🇿🇦", min: 9, max: 9 },
  { code: "+1", name: "United States", flag: "🇺🇸", min: 10, max: 10 },
  { code: "+44", name: "United Kingdom", flag: "🇬🇧", min: 10, max: 10 },
  { code: "+61", name: "Australia", flag: "🇦🇺", min: 9, max: 9 },
  { code: "+263", name: "Zimbabwe", flag: "🇿🇼", min: 9, max: 9 },
  { code: "+260", name: "Zambia", flag: "🇿🇲", min: 9, max: 9 },
  { code: "+267", name: "Botswana", flag: "🇧🇼", min: 8, max: 8 },
  { code: "+264", name: "Namibia", flag: "🇳🇦", min: 8, max: 8 },
  { code: "+266", name: "Lesotho", flag: "🇱🇸", min: 8, max: 8 },
  { code: "+268", name: "Eswatini", flag: "🇸🇿", min: 8, max: 8 },
  { code: "+258", name: "Mozambique", flag: "🇲🇿", min: 9, max: 9 },
  { code: "+265", name: "Malawi", flag: "🇲🇼", min: 9, max: 9 },
  { code: "+254", name: "Kenya", flag: "🇰🇪", min: 9, max: 9 },
  { code: "+234", name: "Nigeria", flag: "🇳🇬", min: 10, max: 10 },
  { code: "+233", name: "Ghana", flag: "🇬🇭", min: 9, max: 9 },
  { code: "+91", name: "India", flag: "🇮🇳", min: 10, max: 10 },
  { code: "+49", name: "Germany", flag: "🇩🇪", min: 10, max: 11 },
  { code: "+33", name: "France", flag: "🇫🇷", min: 9, max: 9 },
  { code: "+31", name: "Netherlands", flag: "🇳🇱", min: 9, max: 9 },
  { code: "+64", name: "New Zealand", flag: "🇳🇿", min: 9, max: 10 },
];

/**
 * Parse a stored phone number like `+27821234567` into a country code
 * and the remaining digits.
 */
function parseStoredPhone(raw) {
  const fallback = { code: "+27", digits: "" };
  if (!raw || typeof raw !== "string") return fallback;

  const sorted = [...COUNTRY_CODES].sort(
    (a, b) => b.code.length - a.code.length
  );

  for (const c of sorted) {
    if (raw.startsWith(c.code)) {
      return {
        code: c.code,
        digits: raw.slice(c.code.length).replace(/\D/g, ""),
      };
    }
  }

  return {
    code: "+27",
    digits: raw.replace(/\D/g, ""),
  };
}

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [locationStatus, setLocationStatus] = useState({
    hasLocation: false,
    isStale: false,
    loading: false,
  });
  const [ethnicities, setEthnicities] = useState([]);
  const [countries, setCountries] = useState([]);
  const profilePicInputRef = useRef(null);

  // WhatsApp number state
  const [phoneCountryCode, setPhoneCountryCode] = useState("+27");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneErrorDialogOpen, setPhoneErrorDialogOpen] = useState(false);
  const [phoneErrorDialogMessage, setPhoneErrorDialogMessage] = useState("");

  // Special-info consent dialog (POPIA s26-27)
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [consentErrorMessage, setConsentErrorMessage] = useState("");

  useEffect(() => {
    loadProfile();
    checkLocationStatus();
    api.get("/lookup/ethnicities").then(res => setEthnicities(res.data)).catch(() => {});
    getCountries().then(data => {
      if (Array.isArray(data)) {
        setCountries(data.map(c => c.name).sort());
      }
    }).catch(() => {});
  }, []);

  const loadProfile = async () => {
    try {
      const data = await getProfile();
      setProfile(data);
      setForm(data);

      const parsed = parseStoredPhone(data.phone_number);
      setPhoneCountryCode(parsed.code);
      setPhoneDigits(parsed.digits);
    } catch (err) {
      toast.error("Failed to load profile");
    }
  };

  const checkLocationStatus = async () => {
    try {
      const status = await getLocationStatus();
      setLocationStatus({
        hasLocation: status.has_location,
        isStale: status.is_stale || false,
        lastUpdated: status.last_updated,
        loading: false,
      });
    } catch (err) {
      console.error("Failed to check location status:", err);
    }
  };

  const handleRefreshLocation = async () => {
    setLocationStatus(prev => ({ ...prev, loading: true }));
    try {
      const result = await requestAndUpdateGPS();
      if (result.ok) {
        toast.success("Location updated!", { description: `📍 ${result.city}, ${result.country}` });
        await loadProfile();
        await checkLocationStatus();
        await refresh();
        return;
      }
    } catch (gpsError) {
      try {
        const ipResult = await ipLocationFallback();
        if (ipResult.ok) {
          toast.warning("Using approximate location", { description: "For better accuracy, please enable GPS" });
          await loadProfile();
          await checkLocationStatus();
          await refresh();
          return;
        }
      } catch (ipError) {}
    }
    setLocationStatus(prev => ({ ...prev, loading: false }));
    toast.error("Unable to update location", { description: "Please enable location services" });
  };

  const handlePhoneDigitsChange = (e) => {
    const raw = e.target.value.replace(/[^\d\s]/g, "");
    setPhoneDigits(raw);
    setPhoneError("");
    const selected = COUNTRY_CODES.find(c => c.code === phoneCountryCode);
    if (selected) {
      const digits = raw.replace(/\D/g, "");
      if (digits.length > selected.max) {
        setPhoneError(`Number should be ${selected.min}-${selected.max} digits for ${selected.name}.`);
      }
    }
  };

  const handleCountryCodeChange = (e) => {
    setPhoneCountryCode(e.target.value);
    setPhoneError("");
    const selected = COUNTRY_CODES.find(c => c.code === e.target.value);
    if (selected && phoneDigits) {
      const digits = phoneDigits.replace(/\D/g, "");
      if (digits.length > selected.max) {
        setPhoneError(`Number should be ${selected.min}-${selected.max} digits for ${selected.name}.`);
      }
    }
  };

  const validatePhone = () => {
    if (!phoneDigits.trim()) return true;
    const selected = COUNTRY_CODES.find(c => c.code === phoneCountryCode);
    if (!selected) return "Please select a valid country code.";
    const digits = phoneDigits.replace(/\D/g, "");
    if (digits.length < selected.min || digits.length > selected.max) {
      return `Number should be ${selected.min}-${selected.max} digits for ${selected.name}.`;
    }
    return true;
  };

  // Actually perform the save (extracted so the consent flow can retry)
  const doSave = async () => {
    setSaving(true);
    try {
      const updateData = { ...form };
      delete updateData.country;
      delete updateData.city;
      delete updateData.latitude;
      delete updateData.longitude;
      updateData.phone_number = phoneDigits.trim()
        ? `${phoneCountryCode}${phoneDigits.replace(/\D/g, "")}`
        : "";
      await updateProfile(updateData);
      await refresh();
      setProfile({ ...form, phone_number: updateData.phone_number });
      setEditing(false);
      toast.success("Profile updated!");
    } catch (err) {
      // Backend returns 403 if health_status set without consent
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "");
      if (err.response?.status === 403 && /special/i.test(msg)) {
        setConsentErrorMessage(msg);
        setConsentDialogOpen(true);
      } else {
        toast.error(msg || "Failed to save");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const validationResult = validatePhone();
    if (validationResult !== true) {
      setPhoneErrorDialogMessage(validationResult);
      setPhoneErrorDialogOpen(true);
      return;
    }

    // POPIA s26-27: health_status is special personal information.
    const rawHealth = (form.health_status || "").toString().trim();
    const willSetHealth = rawHealth !== "" && rawHealth.toLowerCase() !== "none";
    if (willSetHealth && !user?.special_info_consent) {
      setConsentErrorMessage("");
      setConsentDialogOpen(true);
      return;
    }

    await doSave();
  };

  const handleConsentAccept = async () => {
    setConsentSubmitting(true);
    try {
      const version = user?.current_consent_version;
      if (!version) {
        throw new Error("Missing current consent version");
      }
      await api.post("/accept-special-consent", { version });
      await refresh();
      setConsentDialogOpen(false);
      // Retry the save now that consent is recorded
      await doSave();
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
        err.message ||
        "Could not record consent. Please try again."
      );
    } finally {
      setConsentSubmitting(false);
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => update("profile_image", reader.result);
    reader.readAsDataURL(file);
  };

  const hoursRemaining = useMemo(() => {
    if (!user?.created_at) return null;
    try {
      const created = new Date(user.created_at);
      const now = new Date();
      const diffHours = Math.floor((now - created) / (1000 * 60 * 60));
      return Math.max(0, 24 - diffHours);
    } catch {
      return null;
    }
  }, [user?.created_at]);

  const handleDeleteAccount = async () => {
    if (!window.confirm("Permanently delete your account and all data? This cannot be undone.")) return;
    try {
      await api.delete("/auth/me");
      toast.success("Account permanently deleted");
      await logout();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete account");
    }
  };

  if (!profile) return <div className="p-8 text-center">Loading...</div>;

  const age = profile.date_of_birth
    ? new Date().getFullYear() - new Date(profile.date_of_birth).getFullYear()
    : null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-semibold">My Profile</h1>
        <button onClick={() => setEditing(!editing)} className={`neo-btn ${editing ? "neo-btn-secondary" : ""}`}>
          {editing ? <><X className="w-4 h-4" /> Cancel</> : <><Edit3 className="w-4 h-4" /> Edit</>}
        </button>
      </div>

      {/* Location Status Card */}
      <div className={`rounded-xl p-4 mb-4 flex items-center justify-between ${locationStatus.hasLocation && !locationStatus.isStale ? 'bg-[#E8F1EC] border border-[#3A7D44]' : 'bg-[#FEFCE8] border border-[#FDE68A]'}`}>
        <div className="flex items-center gap-3">
          <MapPin className={`w-5 h-5 ${locationStatus.hasLocation && !locationStatus.isStale ? 'text-[#3A7D44]' : 'text-[#B8860B]'}`} />
          <div>
            <div className="text-sm font-medium">
              {!locationStatus.hasLocation ? "Location not set" : locationStatus.isStale ? "Location expired" : "Location active"}
            </div>
            <div className="text-xs text-[#6B6B70]">
              {profile.country ? `${profile.city ? `${profile.city}, ` : ''}${profile.country}` : "Using GPS location"}
            </div>
          </div>
        </div>
        <button
          onClick={handleRefreshLocation}
          disabled={locationStatus.loading}
          className="flex items-center gap-1 text-xs bg-white border border-[#E7E5E0] px-3 py-1.5 rounded-lg hover:bg-[#F5F3EE] transition-colors"
        >
          {locationStatus.loading ? (
            <><div className="w-3 h-3 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" /> Updating...</>
          ) : (
            <><RefreshCw className="w-3 h-3" /> Update Location</>
          )}
        </button>
      </div>

      {/* Avatar */}
      <div className="bg-white border border-[#E7E5E0] rounded-2xl p-5 mb-4">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden border-2">
              <img src={form.profile_image || user?.picture} alt="" className="w-full h-full object-cover" />
            </div>
            {editing && (
              <button
                onClick={() => profilePicInputRef.current?.click()}
                className="absolute bottom-0 right-0 w-8 h-8 bg-[#0F0F10] rounded-full flex items-center justify-center text-white"
              >
                <Camera className="w-4 h-4" />
              </button>
            )}
            <input
              ref={profilePicInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleProfilePicChange}
            />
          </div>
          <div className="flex-1">
            {editing ? (
              <input
                value={form.display_name || ""}
                onChange={e => update("display_name", e.target.value)}
                className="neo-input text-xl font-semibold"
              />
            ) : (
              <h2 className="text-xl font-semibold">{profile.display_name || profile.name}</h2>
            )}
            <p className="text-sm text-[#6B6B70]">{user?.email}</p>
            {age && <p className="text-sm text-[#6B6B70]">{age} years</p>}
          </div>
        </div>
      </div>

      {/* Phone Number (WhatsApp) */}
      <div className="bg-white border border-[#E7E5E0] rounded-2xl p-5 mb-4">
        <label className="text-xs font-semibold uppercase">Phone Number (WhatsApp)</label>
        {editing ? (
          <div className="mt-2 space-y-2">
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={phoneCountryCode}
                onChange={handleCountryCodeChange}
                className="neo-input w-full sm:w-1/2"
              >
                {COUNTRY_CODES.map(c => (
                  <option key={c.code + c.name} value={c.code}>
                    {c.flag} {c.name} ({c.code})
                  </option>
                ))}
              </select>
              <input
                type="tel"
                value={phoneDigits}
                onChange={handlePhoneDigitsChange}
                placeholder="e.g. 82 123 4567"
                className="neo-input w-full sm:w-1/2"
                maxLength={15}
              />
            </div>
            {phoneError && <p className="text-xs text-red-500">{phoneError}</p>}
            <p className="text-xs text-[#6B6B70]">
              We'll store it as {phoneCountryCode}{phoneDigits ? phoneDigits.replace(/\D/g, "") : ""}
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-2 mt-1">
            {profile.phone_number ? (
              <>
                <Phone className="w-4 h-4 text-green-500" />
                <span className="text-sm">{profile.phone_number}</span>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-500">
                  Add your WhatsApp number to receive messages from matches
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Basic Info */}
      <div className="bg-white border border-[#E7E5E0] rounded-2xl p-5 mb-4 space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase">Date of Birth</label>
          {editing ? (
            <input
              type="date"
              value={form.date_of_birth || ""}
              onChange={e => update("date_of_birth", e.target.value)}
              className="neo-input mt-1"
            />
          ) : (
            <p className="text-sm">{profile.date_of_birth || "Not set"}</p>
          )}
        </div>

        <div>
          <label className="text-xs font-semibold uppercase">Gender</label>
          {editing ? (
            <div className="flex gap-2 mt-1">
              {["Male", "Female", "Non-binary", "Other"].map(g => (
                <button
                  key={g}
                  onClick={() => update("gender", g)}
                  className={`px-3 py-1.5 rounded-full text-sm border ${form.gender === g ? "bg-purple-600 text-white border-purple-600" : "bg-white border-[#E7E5E0]"}`}
                >
                  {g}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm">{profile.gender || "Not set"}</p>
          )}
        </div>

        {/* Health Status — special personal information (POPIA s26-27) */}
        <div>
          <label className="text-xs font-semibold uppercase flex items-center gap-1">
            Health Status
            <ShieldCheck className="w-3.5 h-3.5 text-purple-600" />
          </label>
          {editing ? (
            <div className="flex flex-wrap gap-2 mt-1">
              {["HIV", "HPV", "HSV", "Other STD"].map(h => (
                <button
                  key={h}
                  onClick={() => update("health_status", form.health_status === h ? "" : h)}
                  className={`px-3 py-1.5 rounded-full text-sm border ${
                    form.health_status === h
                      ? "bg-purple-600 text-white border-purple-600"
                      : "bg-white border-[#E7E5E0]"
                  }`}
                >
                  {h}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm">{profile.health_status || "Not set"}</p>
          )}
          <p className="text-[10px] text-[#6B6B70] mt-1">
            {user?.special_info_consent
              ? "You've consented to processing this information. Visible only to matches you approve."
              : "You'll be asked for consent the first time you set this."}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase">Positive Since</label>
            {editing ? (
              <input type="text" value={form.positive_since || ""} onChange={e => update("positive_since", e.target.value)} className="neo-input mt-1" placeholder="e.g. 2020" />
            ) : <span className="text-sm">{profile.positive_since || "Not set"}</span>}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase">Height</label>
            {editing ? (
              <input type="text" value={form.height || ""} onChange={e => update("height", e.target.value)} className="neo-input mt-1" placeholder={"e.g. 5'10\""} />
            ) : <span className="text-sm">{profile.height || "Not set"}</span>}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase">Ethnicity</label>
            {editing ? (
              <select value={form.ethnicity || ""} onChange={e => update("ethnicity", e.target.value)} className="neo-input mt-1">
                <option value="">Select</option>
                {ethnicities.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            ) : <span className="text-sm">{profile.ethnicity || "Not set"}</span>}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase">Religion</label>
            {editing ? (
              <input type="text" value={form.religion || ""} onChange={e => update("religion", e.target.value)} className="neo-input mt-1" placeholder="e.g. Christian" />
            ) : <span className="text-sm">{profile.religion || "Not set"}</span>}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase">Sexual Orientation</label>
          {editing ? (
            <div className="flex gap-2 mt-1">
              {["Straight", "Gay", "Lesbian", "Bisexual"].map(o => (
                <button key={o} onClick={() => update("sexual_orientation", o)} className={`px-3 py-1 rounded-full text-sm border ${form.sexual_orientation === o ? "bg-[#7C3AED] text-white border-[#7C3AED]" : "bg-white border-[#E7E5E0]"}`}>{o}</button>
              ))}
            </div>
          ) : <span className="text-sm">{profile.sexual_orientation || "Not set"}</span>}
        </div>

        <div className="flex items-center gap-3">
          <GraduationCap className="w-5 h-5 text-[#6B6B70]" />
          {editing ? (
            <select value={form.education || ""} onChange={e => update("education", e.target.value)} className="neo-input flex-1">
              <option value="">Education</option>
              <option>High School</option><option>Diploma</option><option>Bachelor</option>
              <option>Master</option><option>PhD</option>
            </select>
          ) : <span className="text-sm">{profile.education || "Not set"}</span>}
        </div>
        <div className="flex items-center gap-3">
          <Baby className="w-5 h-5 text-[#6B6B70]" />
          {editing ? (
            <div className="flex gap-2 flex-1">
              <select value={form.kids || ""} onChange={e => update("kids", e.target.value)} className="neo-input">
                <option value="">Kids?</option>
                <option value="have">I have kids</option><option value="none">No kids</option>
              </select>
              <select value={form.want_kids || ""} onChange={e => update("want_kids", e.target.value)} className="neo-input">
                <option value="">Want kids?</option>
                <option value="yes">Yes</option><option value="no">No</option><option value="maybe">Maybe</option>
              </select>
            </div>
          ) : (
            <span className="text-sm">
              {profile.kids === "have" ? "Has kids" : profile.kids === "none" ? "No kids" : ""}
              {profile.want_kids ? ` · Wants kids: ${profile.want_kids}` : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Cigarette className="w-5 h-5 text-[#6B6B70]" />
          {editing ? (
            <select value={form.smoke || ""} onChange={e => update("smoke", e.target.value)} className="neo-input flex-1">
              <option value="">Smoking</option>
              <option value="yes">Smokes</option><option value="no">Non-smoker</option><option value="sometimes">Sometimes</option>
            </select>
          ) : <span className="text-sm">{profile.smoke ? `Smoking: ${profile.smoke}` : "Not set"}</span>}
        </div>
        <div className="flex items-center gap-3">
          <Wine className="w-5 h-5 text-[#6B6B70]" />
          {editing ? (
            <select value={form.drink || ""} onChange={e => update("drink", e.target.value)} className="neo-input flex-1">
              <option value="">Drinking</option>
              <option value="yes">Drinks</option><option value="no">Non-drinker</option><option value="socially">Socially</option>
            </select>
          ) : <span className="text-sm">{profile.drink ? `Drinking: ${profile.drink}` : "Not set"}</span>}
        </div>
        <div className="flex items-center gap-3">
          <Briefcase className="w-5 h-5 text-[#6B6B70]" />
          {editing ? (
            <select value={form.employment || ""} onChange={e => update("employment", e.target.value)} className="neo-input flex-1">
              <option value="">Employment</option>
              <option value="employed">Employed</option><option value="unemployed">Unemployed</option>
              <option value="student">Student</option><option value="self">Self-employed</option>
            </select>
          ) : <span className="text-sm">{profile.employment || "Not set"}</span>}
        </div>
      </div>

      {/* Bio, Interests, Looking For */}
      <div className="bg-white border border-[#E7E5E0] rounded-2xl p-5 mb-4 space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase">Bio</label>
          {editing ? (
            <textarea value={form.bio || ""} onChange={e => update("bio", e.target.value)} className="neo-input mt-1 h-20 resize-none" maxLength={500} />
          ) : <p className="text-sm mt-1">{profile.bio || "No bio"}</p>}
        </div>
        <div>
          <label className="text-xs font-semibold uppercase">Interests</label>
          {editing ? (
            <input value={form.interests || ""} onChange={e => update("interests", e.target.value)} className="neo-input mt-1" placeholder="Travel, Music, Fitness" />
          ) : (
            <div className="flex flex-wrap gap-1 mt-1">
              {(profile.interests || "").split(",").filter(Boolean).map((i, idx) => <span key={idx} className="chip chip-mint text-xs">{i.trim()}</span>)}
              {!profile.interests && <span className="text-sm text-[#6B6B70]">Not set</span>}
            </div>
          )}
        </div>
        <div>
          <label className="text-xs font-semibold uppercase">Looking For</label>
          {editing ? (
            <input value={form.looking_for || ""} onChange={e => update("looking_for", e.target.value)} className="neo-input mt-1" placeholder="Friendship, Dating" />
          ) : <p className="text-sm mt-1">{profile.looking_for || "Not set"}</p>}
        </div>
      </div>

      {/* Gallery */}
      <div className="bg-white border border-[#E7E5E0] rounded-2xl p-5 mb-4">
        <label className="text-xs font-semibold uppercase mb-2 block">Gallery</label>
        {editing ? (
          <ImageUpload images={form.gallery_images || []} onChange={(imgs) => update("gallery_images", imgs)} maxImages={5} />
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {(profile.gallery_images || []).map((img, i) => (
              <div key={i} className="aspect-square rounded-lg overflow-hidden border"><img src={img} alt="" className="w-full h-full object-cover" /></div>
            ))}
            {(!profile.gallery_images || profile.gallery_images.length === 0) && <p className="col-span-3 text-sm text-[#6B6B70]">No gallery images</p>}
          </div>
        )}
      </div>

      {editing && (
        <button onClick={handleSave} disabled={saving} className="neo-btn w-full bg-[#7C3AED] border-[#7C3AED]">
          {saving ? "Saving..." : "Save Changes"} <Save className="w-4 h-4" />
        </button>
      )}

      {/* Data Requests — POPIA s23/s24 (only shown when not editing) */}
      {!editing && (
        <div className="mt-6 bg-white border border-[#E7E5E0] rounded-2xl p-5">
          <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-purple-600" />
            Your data
          </h3>
          <p className="text-xs text-[#6B6B70] mb-3">
            Under POPIA you have the right to access, correct, or delete your
            personal information. We respond within 30 days.
          </p>
          <div className="flex flex-col gap-2">
            <a
              href="mailto:privacy@havenpositive.online?subject=Data%20Export%20Request"
              className="flex items-center gap-2 text-sm text-purple-600 hover:underline"
            >
              <Mail className="w-3.5 h-3.5" /> Request a copy of my data
            </a>
            <a
              href="mailto:privacy@havenpositive.online?subject=Data%20Deletion%20Request"
              className="flex items-center gap-2 text-sm text-purple-600 hover:underline"
            >
              <Mail className="w-3.5 h-3.5" /> Request deletion of my data
            </a>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-[#6B6B70] hover:text-[#0F0F10]"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> View Privacy Policy &amp; Terms
            </a>
          </div>
        </div>
      )}

      {/* Delete Account */}
      <div className="mt-8 text-center">
        {hoursRemaining === null ? (
          <button
            onClick={handleDeleteAccount}
            className="text-sm text-red-500 hover:text-red-600 underline flex items-center justify-center gap-1"
          >
            <Trash2 className="w-4 h-4" /> Delete Account
          </button>
        ) : hoursRemaining > 0 ? (
          <div className="space-y-2">
            <Lock className="w-4 h-4 text-gray-400 mx-auto" />
            <p className="text-sm text-gray-500">
              Delete account available in {hoursRemaining} hour{hoursRemaining !== 1 ? 's' : ''}
            </p>
            <p className="text-xs text-gray-400">
              You can delete your account after 24 hours of membership.
            </p>
          </div>
        ) : (
          <button
            onClick={handleDeleteAccount}
            className="text-sm text-red-500 hover:text-red-600 underline flex items-center justify-center gap-1"
          >
            <Trash2 className="w-4 h-4" /> Delete Account
          </button>
        )}
      </div>

      {/* Phone validation error dialog */}
      <AlertDialog open={phoneErrorDialogOpen} onOpenChange={setPhoneErrorDialogOpen}>
        <AlertDialogContent className="bg-white rounded-2xl shadow-xl border border-[#E7E5E0] max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#0F0F10]">
              <AlertCircle className="w-5 h-5 text-red-500" /> Invalid Phone Number
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#404040] text-sm">
              {phoneErrorDialogMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setPhoneErrorDialogOpen(false)}
              className="bg-purple-600 text-white hover:bg-purple-700"
            >
              Got it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Special-info consent dialog (POPIA s26-27) */}
      <AlertDialog
        open={consentDialogOpen}
        onOpenChange={(open) => {
          if (!consentSubmitting) setConsentDialogOpen(open);
        }}
      >
        <AlertDialogContent className="bg-white rounded-2xl shadow-xl border border-[#E7E5E0] max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#0F0F10]">
              <ShieldCheck className="w-5 h-5 text-purple-600" />
              Consent required
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#404040] text-sm leading-relaxed">
              {consentErrorMessage || (
                <>
                  Under POPIA sections 26–27, we need your explicit consent to
                  process your health status (HIV, HPV, HSV, or other STI). This
                  is special personal information.
                  <br /><br />
                  Your status is only shown to matches you approve — never
                  publicly, never to advertisers. You can withdraw consent at any
                  time by clearing your health status.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={consentSubmitting}
              className="border-[#E7E5E0]"
            >
              Not now
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConsentAccept();
              }}
              disabled={consentSubmitting}
              className="bg-purple-600 text-white hover:bg-purple-700"
            >
              {consentSubmitting ? "Saving…" : "I agree"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}